import { useEffect, useState } from 'react';
import FloorplanOverlay from '../buildings/FloorplanOverlay';
import { accessRequestsApi } from '../requests/apiClient';
import type { AccessRequest } from '@api/index';
import AfterHoursRequestForm from './AfterHoursRequestForm';
import { useAuth } from '../auth/AuthContext';

type Area = { key: string; name: string; color?: string; points?: Array<{ x: number; y: number }> };
type Floor = { id: string; name: string; imageUrl: string };
type Building = { id: string; name: string; imageUrl?: string };

export default function AfterHoursDashboard() {
  const { user } = useAuth();
  const [building, setBuilding] = useState<Building | null>(null);
  const [floors, setFloors] = useState<Floor[]>([]);
  const [floor, setFloor] = useState<Floor | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [viewSize, setViewSize] = useState<{ w: number; h: number }>({ w: 100, h: 100 });
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const normalizeBase = (base: string) => {
    if (!base) return '/api';
    try {
      const u = new URL(base);
      if (u.hostname === 'localhost' && !u.pathname.includes('/api')) {
        return base.replace(/\/?$/, '') + '/api';
      }
    } catch {}
    return base;
  };
  const apiBase = normalizeBase((import.meta as any).env.VITE_API_BASE_URL || '/api');

  const selectFloor = (floor: Floor) => {
    setFloor(floor);
    setSelectedArea(null);
  };


  useEffect(() => {
    (async () => {
      const res = await fetch(`${apiBase}/buildings`);
      if (res.ok) {
        const data = await res.json();
        setBuilding(data?.[0] || null);
      }
    })();
  }, []);

  useEffect(() => {
    if (!building) return;
    (async () => {
      const res = await fetch(`${apiBase}/buildings/${building.id}/floors`);
      if (res.ok) {
        const data = await res.json();
        setFloors(data || []);
        setFloor(data?.[0] || null);
      }
    })();
  }, [building?.id]);

  useEffect(() => {
    if (!building || !floor) return;
    (async () => {
      const res = await fetch(`${apiBase}/buildings/${building.id}/floors/${floor.id}`);
      if (res.ok) {
        const data = await res.json();
        setAreas(data.areas || []);
        setSelectedArea((data.areas || [])[0] || null);
        if(user?.role === 'tenant'){
          setSelectedArea(null);
        }
        setViewSize({ w: data.width || 100, h: data.height || 100 });
      } else {
        setAreas([]);
        setSelectedArea(null);
        setViewSize({ w: 100, h: 100 });
      }
    })();
  }, [building?.id, floor?.id]);

  const loadRequests = async () => {
    if (!building) return;
    try {
      setLoading(true);
      const res = await accessRequestsApi.listAccessRequests(undefined, undefined, undefined, building.id, floor?.id);
      const all = res.data?.data ?? [];
      const filtered = selectedArea ? all.filter((r: any) => (r.areas ?? []).includes(selectedArea.key)) : all;
      setRequests(filtered);
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async (startDateTime: number, endDateTime: number, reason: string) => {
    await accessRequestsApi.createAccessRequest({
      buildingId: building?.id || '',
      floorId: floor?.id || '',
      areas: [selectedArea?.key || '*'],
      requesterUserId: user?.id || '',
      startDateTime,
      endDateTime,
      reason,
    });
    loadRequests();
  };

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building?.id, selectedArea?.key, floor?.id]);


  return (
    <div className="ah-grid">

          {/* Main */}
          <main className="space-y-6" style={{  }}>
            {/* Floor plan with clickable areas */}
            <div className="ah-card">
              <div className="ah-title">{building?.name} - {floor?.name}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 4fr', gap: 16, marginTop: 16}}>
                <div className="ah-grid-col-1 ah-floor-list-container"> 
                  {floors.map(f => (
                    <button key={f.id} onClick={() => selectFloor(f)} className={`ah-floor-pill ${floor && f.id === floor.id ? 'active' : ''}`}>{f.name}</button>
                  ))}
                </div>
                <div className="ah-grid-col-3"> 
                  <FloorplanOverlay
                    imageUrl={floor?.imageUrl || ''}
                    areas={user?.role === 'admin' || user?.role === 'manager'? areas as any : []}
                    selectedKey={selectedArea?.key}
                    onSelect={(key) => {
                      const next = areas.find(a => a.key === key) || null;
                      if(user?.role === 'admin' || user?.role === 'manager'){
                        setSelectedArea(next);
                      }
                    }}
                    viewWidth={viewSize.w}
                    viewHeight={viewSize.h}
                  /> 
                </div>
              </div>
            </div>

            {/* Request form - shows first on mobile */}
            <div className="ah-mobile-request-form">
              <AfterHoursRequestForm
                selectedArea={selectedArea?.key || floor?.name || ''}
                submitRequest={submitRequest}
              />
            </div>

            {/* Existing requests */}
            <div className="ah-card">
              <div className="ah-title">Existing Requests for {selectedArea?.name || floor?.name}</div>
              {loading ? (
                <div className="muted">Loading...</div>
              ) : requests.length === 0 ? (
                <div className="muted">No requests yet.</div>
              ) : (
                <div className="ah-table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Time Range</th>
                        <th>Requested By</th>
                        <th>Floor</th>
                        <th>Areas</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map(r => (
                        <tr key={r.id}>
                          <td>{new Date(r.startDateTime).getDate()}/{new Date(r.startDateTime).getMonth()+1}/{new Date(r.startDateTime).getFullYear()} {new Date(r.startDateTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - {new Date(r.endDateTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</td>
                          <td>{r.requesterUserId}</td>
                          <td>{r.floorId}</td>
                          <td>{(r.areas ?? []).join(', ')}</td>
                          <td>{r.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>

          <aside className="ah-desktop-request-form">
            <AfterHoursRequestForm
              selectedArea={selectedArea?.key || floor?.name || ''}
              submitRequest={submitRequest}
            />
          </aside>
        </div>
  );
}
